import { FC } from 'react';
import { Typography, Box, SxProps, Container, Stack, Link } from '@mui/material';
import { Phone as PhoneIcon, Email as EmailIcon } from '@mui/icons-material';

interface FooterProps {
  sx?: SxProps;
}

export const Footer: FC<FooterProps> = ({ sx }) => {
  const currentYear = new Date().getFullYear();

  const footerSx: SxProps = {
    display: 'flex',
    flexDirection: { xs: 'column-reverse', sm: 'row' },
    alignItems: 'center',
    height: '100%',
    py: 3,
    px: { xs: 2, sm: 6 },
    ...sx,
  };

  return (
    <Container maxWidth="xl">
      <footer>
        <Box sx={footerSx}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'column', md: 'row' },
              gap: { sm: 2, md: 4 },
              mb: { sm: 3, md: 0 },
              alignItems: { xs: 'flex-start', sm: 'center' },
            }}
          >
            {/* Copyright Notice */}
            <Typography variant="body2" sx={{ mt: { xs: 1, sm: 0 } }}>
              &copy; {currentYear} CARCAREERS.COM
            </Typography>
            {/* Contact Information Section */}
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Link
                href="tel:8883979797"
                sx={{
                  color: 'text.primary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                (888) 397-9797
              </Link>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <EmailIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Link
                href="mailto:info@carcareers.com"
                sx={{
                  color: 'text.primary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                info@carcareers.com
              </Link>
            </Stack>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Legal Links Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                ml: { xs: 0, sm: 3 },
              }}
            >
              <Typography
                variant="body2"
                component="a"
                href="/privacy"
                sx={{
                  textDecoration: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main',
                  },
                }}
              >
                Privacy Policy
              </Typography>
              <Box
                data-testid="footer-divider"
                sx={{
                  mx: 1,
                  borderLeft: 1,
                  height: '1rem',
                  borderColor: 'text.secondary',
                }}
              />
              <Typography
                variant="body2"
                component="a"
                href="/terms"
                sx={{
                  textDecoration: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main',
                  },
                }}
              >
                Terms of Use
              </Typography>
            </Box>
          </Box>
        </Box>
      </footer>
    </Container>
  );
};

export default Footer;
